/**
 * Copy for the upload panel (SPEC §6, FIL-7).
 *
 * Sentence case throughout: `Eyebrow` and `Badge` apply the uppercase and the
 * wide tracking themselves.
 *
 * **Nothing here may promise durability.** Resume is bounded by the page — a
 * `File` handle does not survive a reload — so there is no "you can close this
 * tab", no "we will finish this later", and no Pause. A failed transfer is
 * offered again, not deferred.
 */
export const UPLOAD_COPY = {
  /**
   * A heading rather than an eyebrow.
   *
   * Uploading is the reason a member opens a folder they can write to, and an
   * uppercase micro-label sitting over a dashed box read as a field on a form
   * rather than as the step it is.
   */
  heading: 'Upload files',

  /** Says what the step does, in one line, under the heading. */
  subline: 'Add one or more files to this folder.',

  /** The label on the hidden file input. It is the primary act on this screen. */
  choose: 'Choose files',

  /**
   * The full-viewport drop overlay (FIL-7, ADR-0061).
   *
   * There is no "or drag them here" hint any more, because there is nothing to
   * point at: the target is the page, and it says so the moment files are over
   * it rather than waiting to be found.
   *
   * **The headline is a fixed string in both branches and never interpolates.**
   * It is the overlay's accessible name, and every test and e2e query matches it
   * exactly; a folder name inside it would make the name a moving target.
   */
  drop: {
    active: 'Drop files to add them to this folder',
    /** Under the headline, and only when the folder's name is known. */
    activeIn: (folderName: string) => `They will be added to ${folderName}.`,
    /**
     * Everywhere else a `can*` flag simply removes a control. Here it does not:
     * a button's absence is legible, and a gesture that produces no response at
     * all reads as a broken page rather than as a refusal. The detail line
     * beneath is `FOLDER_COPY.states.readOnly`, reused verbatim.
     */
    readOnly: 'This folder is read-only',
  },

  /**
   * The one durability statement, and it is a warning rather than a promise.
   * Said up front because the alternative is somebody learning it at 90%.
   *
   * **It gets louder while bytes are moving and it never gets softer.** Resume
   * is bounded by the page — a `File` handle does not survive a reload — so the
   * moment this actually matters is the moment a transfer is in flight. Nothing
   * here may drift toward reassurance (ADR-0009).
   */
  stayOnPage: 'Keep this page open until each transfer finishes.',

  queue: {
    heading: 'Transfers',
    /** Progress bar's accessible name. */
    progress: (fileName: string) => `Sending ${fileName}`,
    waiting: 'Waiting',
    opening: 'Starting…',
    staging: 'Sending',
    committing: 'Assembling…',
    finalizing: 'Finishing…',
    done: 'Added',
    /** Only when the API gave nothing more specific. */
    failed: 'That file could not be sent.',
    retry: 'Try again',
    /** On a failure no retry could fix — a observer space, an unprovisioned agency. */
    permanent: 'Trying again will not help.',
    dismiss: (fileName: string) => `Remove ${fileName} from this list`,
    of: (sent: string, total: string) => `${sent} of ${total}`,
  },

  refusals: {
    empty: (fileName: string) => `${fileName} is empty, so there is nothing to send.`,
    tooLarge: (fileName: string) => `${fileName} is larger than a single file can be.`,
    /**
     * A dropped directory, and it must be refused **before** `empty`: a
     * directory's `getAsFile()` yields a zero-byte `File`, so without its own
     * sentence it is reported as an empty file — true about the wrong thing.
     * There are no nested folders (FIL-2), so the remedy is to drop the
     * contents.
     */
    folder: (name: string) =>
      `${name} is a folder, so it cannot be sent. Drop the files inside it instead.`,
  },
} as const;
